import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../lib/api';
import {
  deriveKeyFromPassword,
  decryptPrivateKey,
  storeEncryptionKeys,
} from '../lib/crypto';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Loader2 } from 'lucide-react';

/**
 * Dialog that prompts the user for their password to re-derive the
 * zero-knowledge private key when it's missing from sessionStorage
 * (e.g. after a hard refresh). On success, the key is restored to
 * sessionStorage and `onUnlocked(privateKey)` is invoked.
 */
export const PasswordUnlockDialog = ({ open, onOpenChange, onUnlocked, reason }) => {
  const { user } = useAuth();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setPassword('');
    setError('');
    setLoading(false);
  };

  const handleClose = (next) => {
    if (loading) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!password || !user?.email) return;

    setLoading(true);
    setError('');
    try {
      const { data } = await authApi.unlock({ email: user.email, password });
      const { encrypted_private_key, encryption_salt } = data;

      if (!encrypted_private_key || !encryption_salt) {
        throw new Error('Server did not return encryption material');
      }

      // Derive the password key locally; the server never sends it.
      const pdk = deriveKeyFromPassword(password, encryption_salt);
      const privateKey = await decryptPrivateKey(encrypted_private_key, pdk);

      storeEncryptionKeys({ privateKey, encryptionKey: pdk });

      reset();
      // Unlocked first, then closed: a caller waiting on the unlock (UnlockGate)
      // treats a close without it as "Not now".
      onUnlocked?.(privateKey);
      onOpenChange(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setError('That password is not right. Try again.');
      } else {
        setError('We could not unlock your records. Try again.');
      }
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="password-unlock-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[24px] font-semibold tracking-[-0.02em]">
            Unlock your records
          </DialogTitle>
          <DialogDescription className="text-ink-70 text-base">
            {reason ||
              'Your key is only kept for this browser session. Enter your password to open your records again.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="p-4 rounded-lg bg-inset text-ink-70 text-sm leading-relaxed">
            The unlocking happens in your browser. We only check the password; the key that opens
            your records never leaves this device.
          </p>

          <div className="space-y-2">
            <Label htmlFor="unlock-password" className="text-[15px] font-semibold block">What&apos;s your password?</Label>
            <Input
              id="unlock-password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError('');
              }}
              autoFocus
              autoComplete="current-password"
              aria-invalid={error ? true : undefined}
              data-testid="password-unlock-input"
            />
            {error && (
              <p className="text-sm text-breach-ink" data-testid="password-unlock-error">
                {error}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleClose(false)}
              disabled={loading}
              data-testid="password-unlock-cancel"
            >
              Not now
            </Button>
            <Button
              type="submit"
              disabled={!password || loading}
              data-testid="password-unlock-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Unlocking
                </>
              ) : (
                'Unlock'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordUnlockDialog;
